import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  IconButton,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { FactCheck as FactCheckIcon, Visibility as VisibilityIcon } from '@mui/icons-material';
import { useAuth } from '../../context/AuthContext';
import { formatDate, formatDateTime, formatTotals } from '../../services/expenseVouchersApi';
import { approvalsApi, STAGE_META } from '../../services/expenseApprovalsApi';
import { useApprovalsBase, GROUP_LABELS } from '../../components/expense/voucherShared';

const SCOPES = [
  { value: '', label: 'Everything waiting for me' },
  { value: 'accounts', label: 'Accounts review', accountsOnly: true },
  { value: 'approver', label: 'My approvals' },
];

// What the signed-in user is expected to do with the row (from the server's actions)
const pendingFor = (row) => {
  if (row.actions?.canApprove) {
    return `${row.actions.approveGroups.map((g) => GROUP_LABELS[g]).join(' + ')} approval`;
  }
  if (row.stage === 'BLOCKED') return 'Re-route (inactive approver)';
  if (row.actions?.canRoute) return 'Accounts review';
  return '-';
};

// Shared approval queue: Accounts, Reporting and Management approvers. The server returns
// only vouchers that need an action from the signed-in user.
const ExpenseApprovals = () => {
  const navigate = useNavigate();
  const base = useApprovalsBase();
  const { user } = useAuth();
  const isAccounts = user?.access?.expenseAccounts === true;
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [scope, setScope] = useState('');
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search.trim()), 300);
    return () => clearTimeout(t);
  }, [search]);

  useEffect(() => { setPage(0); }, [debouncedSearch, scope]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await approvalsApi.list({ search: debouncedSearch, scope, page: page + 1, limit: rowsPerPage });
      setRows(res.data || []);
      setTotal(res.pagination?.totalItems || 0);
      setLoadError(null);
    } catch (e) {
      setRows([]);
      setLoadError(e);
    } finally {
      setLoading(false);
    }
  }, [debouncedSearch, scope, page, rowsPerPage]);

  useEffect(() => { load(); }, [load]);

  const scopes = SCOPES.filter((s) => !s.accountsOnly || isAccounts);
  const columns = ['Voucher No.', 'Employee', 'Department', 'Voucher Date', 'Submitted', 'Total', 'Stage', 'Your action', ''];

  return (
    <Box sx={{ bgcolor: 'background.default', p: 3 }}>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" component="h1" sx={{ fontWeight: 600, fontSize: '30px', lineHeight: 1.5, letterSpacing: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
          <FactCheckIcon color="primary" />
          Expense Approvals
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Expense vouchers waiting for your action{isAccounts ? ' as Accounts or as an approver' : ''}.
        </Typography>
      </Box>

      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 2 }}>
        <TextField
          size="small"
          placeholder="Search voucher no., employee, department..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ minWidth: 320 }}
        />
        <TextField
          select
          size="small"
          label="Show"
          value={scope}
          onChange={(e) => setScope(e.target.value)}
          sx={{ minWidth: 220 }}
          SelectProps={{ inputProps: { 'data-testid': 'scope-filter' } }}
        >
          {scopes.map((s) => <MenuItem key={s.value} value={s.value}>{s.label}</MenuItem>)}
        </TextField>
      </Box>

      {loadError && <Alert severity="error" sx={{ mb: 2 }}>Failed to load approvals: {loadError.message}</Alert>}

      <TableContainer component={Paper} sx={{ boxShadow: 2 }}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ backgroundColor: 'primary.main' }}>
              {columns.map((h, i) => (
                <TableCell key={h || i} sx={{ color: 'white', fontWeight: 600, whiteSpace: 'nowrap' }}>{h}</TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={columns.length} align="center"><CircularProgress size={28} /></TableCell></TableRow>
            ) : rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={columns.length} align="center" sx={{ py: 4 }}>
                  <Typography variant="body2" color="text.secondary">
                    {debouncedSearch ? 'No vouchers match your search.' : 'Nothing is waiting for your approval.'}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : rows.map((row) => {
              const stage = STAGE_META[row.stage] || { label: row.stage, color: 'default' };
              return (
                <TableRow key={row._id} hover sx={{ cursor: 'pointer' }} onClick={() => navigate(`${base}/${row._id}`)}>
                  <TableCell sx={{ fontWeight: 600 }}>{row.voucherNumber}</TableCell>
                  <TableCell>{row.employeeSnapshot?.name || '-'}</TableCell>
                  <TableCell>{row.employeeSnapshot?.department || '-'}</TableCell>
                  <TableCell>{formatDate(row.voucherDate)}</TableCell>
                  <TableCell>{formatDateTime(row.submittedAt)}</TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>{formatTotals(row)}</TableCell>
                  <TableCell><Chip size="small" label={stage.label} color={stage.color} /></TableCell>
                  <TableCell>{pendingFor(row)}</TableCell>
                  <TableCell>
                    <Tooltip title="Review">
                      <IconButton size="small" color="primary" aria-label={`Review ${row.voucherNumber}`}>
                        <VisibilityIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
        <TablePagination
          component="div"
          count={total}
          page={page}
          rowsPerPage={rowsPerPage}
          onPageChange={(e, p) => setPage(p)}
          onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
          rowsPerPageOptions={[10, 25, 50, 100]}
        />
      </TableContainer>
    </Box>
  );
};

export default ExpenseApprovals;
